import { Feature, Polygon } from 'geojson'
import { BehaviorSubject, Observable, Subject } from 'rxjs'

import {
  Coordinates,
  GeofenceColor,
  GeofenceStyle,
  LatLngCoords,
  LngLatCoords,
  LocationType,
  SupportedShape,
} from '@ti/contracts'

import { GeofenceEditMarkerStep } from './types'

export type GeofenceData = {
  id: string
  name?: string
  type?: LocationType
  shape?: SupportedShape
  color?: GeofenceColor
  style?: GeofenceStyle
  address?: string
  // Circle properties
  isCircle?: boolean
  center?: LatLngCoords
  radiusInKm?: number
  // Corridor properties; the geometry holds the outline, empty until it is built
  isCorridor?: boolean
  path?: LngLatCoords[]
  widthMeters?: number
  isHidden?: boolean
}
export type GeofenceFeature<T = GeofenceData> = Feature<Polygon, T>

export interface GeofenceMeasurement {
  pointCount: number
  isClosed: boolean
  distanceMeters?: number
  areaSquareMeters?: number
}

export interface GeofenceCorridorState {
  pointCount: number
  widthMeters: number
}

export interface GeofenceMarkerState {
  step$: Observable<GeofenceEditMarkerStep | undefined>
  name$: Observable<string>
  color$: Observable<GeofenceColor | undefined>
  shape$: Observable<SupportedShape | undefined>
  offset$: Observable<number>
  isSaving$: Observable<boolean>
  isPinned$: Observable<boolean>
  isHidden$: Observable<boolean>
  measurement$: Observable<GeofenceMeasurement | undefined>
  corridor$: Observable<GeofenceCorridorState | undefined>
  notAllowedNames$: Observable<string[]>
  prevCircleFeature$: Observable<unknown>
  prevPolygonFeature$: Observable<unknown>
  prevCorridorFeature$: Observable<unknown>
}

export interface GeofenceMarkerOptions extends GeofenceData {
  step?: GeofenceEditMarkerStep
  latLng: Coordinates
}
export interface GeofenceMarkerEvents {
  activate$: Subject<void>
  cancel$: Subject<void>
  delete$: Subject<void>
  save$: Subject<{ data: { name?: string; color?: string }; setupAlert?: boolean }>
  setShape$: Subject<SupportedShape>
  setCorridorWidth$: Subject<number>
  measure$: Subject<void>
  clearMeasurement$: Subject<void>
  createFromMeasurement$: Subject<void>
  setColor$: Subject<GeofenceColor>
  pinned$: Subject<boolean>
  isHidden$: Subject<boolean>
}

export abstract class MapGeofenceDataSource {
  public onCreated = new Subject<GeofenceFeature>()
  public onUpdated = new Subject<GeofenceFeature>()
  public onDeleted = new Subject<GeofenceFeature>()

  /**
   * Geofences that should be drawn on the map. Sources that honour the `isHidden`
   * flag drop hidden geofences from this stream.
   */
  public abstract data$: Observable<GeofenceFeature[]>

  /**
   * Every geofence the source knows about, regardless of the `isHidden` flag.
   * Use it for lookups and validation (opening a geofence in the editor, checking
   * for duplicate names); use `data$` for anything that renders.
   */
  public get allData$(): Observable<GeofenceFeature[]> {
    return this.data$
  }

  public readonly dataFilterCb$ = new BehaviorSubject<
    ((items: GeofenceFeature[]) => GeofenceFeature[]) | undefined
  >(undefined)
}

export abstract class MapGeofenceEditDataSource extends MapGeofenceDataSource {
  public abstract addGeofence(data: GeofenceFeature): Promise<GeofenceFeature | undefined>
  public abstract deleteGeofence(data: GeofenceFeature): unknown
  public abstract updateGeofence(data: GeofenceFeature): Promise<GeofenceFeature | undefined>
}
